import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { DatePicker } from '@mui/x-date-pickers'
import { AppDialog, Loader } from '../../../components'
import { useAppDispatch } from '../../../hooks'
import { showAlertAction } from '../../../store/alerts'
import {
  currencyFormatter,
  getEncodedFormattedDate,
  getFormattedDateTimeIST,
} from '../../../utils'
import { getNextInvoiceNo } from './Services'
import {
  createDebitCreditNote,
  CreateDebitCreditNoteRequest,
  DebitCreditNoteLineEntry,
  fetchInterimBillInvoice,
  InterimBillInvoiceDetails,
} from '../../../services/monthlyBills'

const roundToTwo = (value: number): number =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100

const formatAmount = (value: number): string =>
  roundToTwo(value).toFixed(2)

export interface DebitCreditNoteDialogProps {
  open: boolean
  onClose: () => void
  invoiceId: number
  accountId: number
  noteType: 'DBN' | 'CRN'
  onSuccess?: () => void
}

interface NoteLineRow {
  id: string
  itemName: string
  quantity?: number
  days?: number
  rate?: number
  amount: number
}

interface DraftLine {
  itemName: string
  quantity: string
  days: string
  rate: string
  amount: string
}

const emptyDraft = (): DraftLine => ({
  itemName: '',
  quantity: '',
  days: '',
  rate: '',
  amount: '',
})

function parseOptionalInt(value: string): number | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const n = parseInt(trimmed, 10)
  return Number.isNaN(n) ? undefined : n
}

function parseOptionalNumber(value: string): number | undefined {
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const n = Number(trimmed)
  return Number.isNaN(n) ? undefined : n
}

function calcDraftAmount(
  quantity: string,
  days: string,
  rate: string
): number {
  const qty = parseOptionalInt(quantity) ?? 1
  const dur = parseOptionalInt(days) ?? 1
  const r = parseOptionalNumber(rate) ?? 1
  return roundToTwo(qty * dur * r)
}

function computeNoteTotals(
  lines: NoteLineRow[],
  rates: Pick<
    InterimBillInvoiceDetails,
    'cgstRate' | 'sgstRate' | 'igstRate'
  >
) {
  const taxableAmount = roundToTwo(
    lines.reduce((sum, line) => sum + Number(line.amount), 0)
  )
  let cgstValue = 0
  let sgstValue = 0
  let igstValue = 0
  if (rates.igstRate > 0) {
    igstValue = roundToTwo((taxableAmount * rates.igstRate) / 100)
  } else {
    cgstValue = roundToTwo((taxableAmount * rates.cgstRate) / 100)
    sgstValue = roundToTwo((taxableAmount * rates.sgstRate) / 100)
  }
  const noteTotal = roundToTwo(
    taxableAmount + igstValue + cgstValue + sgstValue
  )
  return { taxableAmount, cgstValue, sgstValue, igstValue, noteTotal }
}

function buildLineEntryPayload(line: NoteLineRow): DebitCreditNoteLineEntry {
  const entry: DebitCreditNoteLineEntry = {
    itemName: line.itemName.trim(),
    amount: line.amount,
  }
  if (line.quantity !== undefined && line.quantity !== null) {
    entry.quantity = line.quantity
  }
  if (line.days !== undefined && line.days !== null) {
    entry.duration = line.days
  }
  if (line.rate !== undefined && line.rate !== null) {
    entry.rate = line.rate
  }
  return entry
}

function SummaryRow({
  label,
  value,
  emphasize,
}: {
  label: string
  value: string
  emphasize?: boolean
}) {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 0.35 }}
    >
      <Typography
        variant="body2"
        color={emphasize ? 'text.primary' : 'text.secondary'}
        fontWeight={emphasize ? 600 : 400}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={emphasize ? 700 : 500}
        sx={{ fontVariantNumeric: 'tabular-nums', minWidth: 100, textAlign: 'right' }}
      >
        {value}
      </Typography>
    </Stack>
  )
}

export const DebitCreditNoteDialog: FC<DebitCreditNoteDialogProps> = ({
  open,
  onClose,
  invoiceId,
  accountId,
  noteType,
  onSuccess,
}) => {
  const dispatchAction = useAppDispatch()
  const [invoiceDetails, setInvoiceDetails] =
    useState<InterimBillInvoiceDetails | null>(null)
  const [loadingInvoice, setLoadingInvoice] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [lines, setLines] = useState<NoteLineRow[]>([])
  const [draft, setDraft] = useState<DraftLine>(emptyDraft)
  const [noteDate, setNoteDate] = useState<Date | null>(new Date())
  const [invoiceNo, setInvoiceNo] = useState<string | number>('')
  const [fullInvNo, setFullInvNo] = useState<string | null>(null)
  const [loadingInvoiceNo, setLoadingInvoiceNo] = useState(false)
  const lineIdRef = useRef(0)

  const dialogTitle =
    noteType === 'DBN' ? 'Create Debit Note' : 'Create Credit Note'

  const fetchInvoiceNoByDate = useCallback(
    (date: Date) => {
      if (!accountId || accountId <= 0) {
        return
      }
      setLoadingInvoiceNo(true)
      getNextInvoiceNo(accountId, getEncodedFormattedDate(date), noteType)
        .then((response) => {
          if (response?.success) {
            setInvoiceNo(response.data?.docNo ?? '')
            if (response.data?.fullInvNo != null) {
              setFullInvNo(response.data.fullInvNo)
            } else {
              setFullInvNo(null)
            }
          } else {
            dispatchAction(
              showAlertAction({
                message: response?.message || 'Failed to fetch invoice number.',
                severity: 'error',
              })
            )
          }
        })
        .catch((err: { response?: { data?: { message?: string } }; message?: string }) => {
          dispatchAction(
            showAlertAction({
              message:
                err?.response?.data?.message ||
                err?.message ||
                'Failed to fetch invoice number.',
              severity: 'error',
            })
          )
        })
        .finally(() => {
          setLoadingInvoiceNo(false)
        })
    },
    [accountId, noteType, dispatchAction]
  )

  useEffect(() => {
    if (!open || !invoiceId) {
      return
    }
    let cancelled = false
    setLoadingInvoice(true)
    setInvoiceDetails(null)
    setLines([])
    setDraft(emptyDraft())
    setNoteDate(new Date())
    setInvoiceNo('')
    setFullInvNo(null)
    fetchInterimBillInvoice(invoiceId, noteType)
      .then((res) => {
        if (cancelled) return
        if (res?.success && res.data) {
          setInvoiceDetails(res.data)
          const prefillAmount = res.data.crnDBNValue
          const nextDraft = emptyDraft()
          if (
            prefillAmount != null &&
            Number.isFinite(Number(prefillAmount)) &&
            Number(prefillAmount) > 0
          ) {
            nextDraft.amount = String(roundToTwo(Number(prefillAmount)))
          }
          setDraft(nextDraft)
        } else {
          dispatchAction(
            showAlertAction({
              message: res?.message || 'Failed to load invoice details.',
              severity: 'error',
            })
          )
        }
      })
      .catch((err: { response?: { data?: { message?: string } }; message?: string }) => {
        if (cancelled) return
        dispatchAction(
          showAlertAction({
            message:
              err?.response?.data?.message ||
              err?.message ||
              'Failed to load invoice details.',
            severity: 'error',
          })
        )
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingInvoice(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [open, invoiceId, noteType, dispatchAction])

  useEffect(() => {
    if (!open || !invoiceDetails || !noteDate || !accountId || accountId <= 0) {
      return
    }
    fetchInvoiceNoByDate(noteDate)
  }, [open, invoiceDetails, accountId, fetchInvoiceNoByDate])

  const totals = useMemo(() => {
    if (!invoiceDetails) {
      return null
    }
    return computeNoteTotals(lines, invoiceDetails)
  }, [lines, invoiceDetails])

  const updateDraftField = (field: keyof DraftLine, value: string) => {
    setDraft((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'quantity' || field === 'days' || field === 'rate') {
        next.amount = String(
          calcDraftAmount(next.quantity, next.days, next.rate)
        )
      }
      return next
    })
  }

  const handleAddLine = () => {
    const itemName = draft.itemName.trim()
    if (!itemName) {
      dispatchAction(
        showAlertAction({
          message: 'Item is required.',
          severity: 'warning',
        })
      )
      return
    }
    const amount = parseOptionalNumber(draft.amount)
    if (amount === undefined || amount <= 0) {
      dispatchAction(
        showAlertAction({
          message: 'Amount must be greater than 0.',
          severity: 'warning',
        })
      )
      return
    }
    lineIdRef.current += 1
    setLines((prev) => [
      ...prev,
      {
        id: `line-${lineIdRef.current}`,
        itemName,
        quantity: parseOptionalInt(draft.quantity),
        days: parseOptionalInt(draft.days),
        rate: parseOptionalNumber(draft.rate),
        amount: roundToTwo(amount),
      },
    ])
    setDraft(emptyDraft())
  }

  const handleDeleteLine = (id: string) => {
    setLines((prev) => prev.filter((line) => line.id !== id))
  }

  const handleSubmit = async () => {
    if (!invoiceDetails || submitting || loadingInvoice) {
      return
    }
    if (!noteDate || Number.isNaN(noteDate.getTime())) {
      dispatchAction(
        showAlertAction({
          message: 'Please select note date.',
          severity: 'warning',
        })
      )
      return
    }
    const invoiceNoStr = String(invoiceNo ?? '').trim()
    if (!invoiceNoStr) {
      dispatchAction(
        showAlertAction({
          message: 'Please enter invoice number.',
          severity: 'warning',
        })
      )
      return
    }
    if (!accountId || accountId <= 0) {
      dispatchAction(
        showAlertAction({
          message: 'Account is missing; cannot create note.',
          severity: 'warning',
        })
      )
      return
    }
    if (lines.length < 1) {
      dispatchAction(
        showAlertAction({
          message: 'Add at least one line item.',
          severity: 'warning',
        })
      )
      return
    }
    const { taxableAmount, cgstValue, sgstValue, igstValue, noteTotal } =
      computeNoteTotals(lines, invoiceDetails)

    const body: CreateDebitCreditNoteRequest = {
      invoiceId,
      noteType,
      createDate: getFormattedDateTimeIST(noteDate),
      invoiceNo: invoiceNoStr,
      taxableAmount,
      invoiceTotal: noteTotal,
      cgstRate: invoiceDetails.cgstRate,
      sgstRate: invoiceDetails.sgstRate,
      igstRate: invoiceDetails.igstRate,
      cgstValue,
      sgstValue,
      igstValue,
      lineEntries: lines.map(buildLineEntryPayload),
    }

    setSubmitting(true)
    try {
      const res = await createDebitCreditNote(body)
      if (res?.success) {
        dispatchAction(
          showAlertAction({
            message: res.message || 'Note created successfully.',
            severity: 'success',
          })
        )
        onSuccess?.()
        onClose()
      } else {
        dispatchAction(
          showAlertAction({
            message: res?.message || 'Failed to create note.',
            severity: 'error',
          })
        )
      }
    } catch (err: unknown) {
      const apiErr = err as {
        response?: { data?: { message?: string } }
        message?: string
      }
      dispatchAction(
        showAlertAction({
          message:
            apiErr?.response?.data?.message ||
            apiErr?.message ||
            'Failed to create note.',
          severity: 'error',
        })
      )
    } finally {
      setSubmitting(false)
    }
  }

  const originalTotal = invoiceDetails?.invoiceTotal ?? 0
  const noteTotal = totals?.noteTotal ?? 0
  const finalInvoiceValue =
    noteType === 'DBN'
      ? roundToTwo(originalTotal + noteTotal)
      : roundToTwo(originalTotal - noteTotal)

  const noteValueLabel =
    noteType === 'DBN' ? 'Debit Note Value' : 'Credit Note Value'
  const hasLines = lines.length > 0

  return (
    <AppDialog
      id="debit-credit-note-dialog"
      title={dialogTitle}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitButtonText={submitting ? 'Submitting…' : 'Submit'}
      close={!open}
      maxWidth="md"
      fixHeight
      disableEnter
    >
      {loadingInvoice && <Loader />}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          opacity: loadingInvoice ? 0.5 : 1,
          pointerEvents: loadingInvoice ? 'none' : 'auto',
        }}
      >
        {invoiceDetails && (
          <Grid
            container
            spacing={2}
            alignItems="flex-start"
            sx={{ mb: 0, flexShrink: 0 }}
          >
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Party:</strong> {invoiceDetails.buyerName}
              </Typography>
              <Typography variant="body2">
                <strong>Site:</strong> {invoiceDetails.siteAddress}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Invoice:</strong>{' '}
                {invoiceDetails.invoiceDisplayNumber}
              </Typography>
              <Typography variant="body2">
                <strong>Original Invoice Value:</strong>{' '}
                {currencyFormatter.format(invoiceDetails.invoiceTotal)}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <DatePicker
                label="Note Date"
                value={noteDate}
                onChange={(date) => {
                  setNoteDate(date)
                  if (date) {
                    fetchInvoiceNoByDate(date)
                  }
                }}
                format="dd/MM/yyyy hh:mm a"
                disabled={loadingInvoice || loadingInvoiceNo}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small',
                    variant: 'outlined',
                    error:
                      noteDate != null && Number.isNaN(noteDate.getTime()),
                  },
                }}
              />
              <TextField
                size="small"
                label="Invoice Number"
                fullWidth
                variant="outlined"
                value={invoiceNo}
                onChange={(e) => setInvoiceNo(e.target.value)}
                disabled={loadingInvoice || loadingInvoiceNo}
                sx={{ mt: 1.5 }}
              />
              <Typography color="green" sx={{ fontSize: 12, ml: 0.5, mt: 0.5 }}>
                Full Invoice no: {fullInvNo ?? invoiceNo ?? ''}
              </Typography>
            </Grid>
          </Grid>
        )}

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Grid container spacing={1} alignItems="flex-end" sx={{ mb: 1, mt:0.5 }}>
            <Grid item xs={12} sm={3}>
              <TextField
                size="small"
                label="Item"
                required
                fullWidth
                value={draft.itemName}
                onChange={(e) => updateDraftField('itemName', e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                size="small"
                label="Quantity"
                fullWidth
                type="number"
                inputProps={{ step: 1, min: 0 }}
                value={draft.quantity}
                onChange={(e) => updateDraftField('quantity', e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                size="small"
                label="Days"
                fullWidth
                type="number"
                inputProps={{ step: 1, min: 0 }}
                value={draft.days}
                onChange={(e) => updateDraftField('days', e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                size="small"
                label="Rate"
                fullWidth
                type="number"
                inputProps={{ step: 'any', min: 0 }}
                value={draft.rate}
                onChange={(e) => updateDraftField('rate', e.target.value)}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField
                size="small"
                label="Amount"
                required
                fullWidth
                type="number"
                inputProps={{ step: 'any', min: 0 }}
                value={draft.amount}
                onChange={(e) => updateDraftField('amount', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={1}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddLine}
                disabled={loadingInvoice || !invoiceDetails}
                sx={{ textTransform: 'none', minWidth: 56, height: 40 }}
              >
                ADD
              </Button>
            </Grid>
          </Grid>

          {hasLines ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Days</TableCell>
                    <TableCell align="right">Rate</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lines.map((line) => (
                    <TableRow key={line.id}>
                      <TableCell>{line.itemName}</TableCell>
                      <TableCell align="right">
                        {line.quantity ?? ''}
                      </TableCell>
                      <TableCell align="right">{line.days ?? ''}</TableCell>
                      <TableCell align="right">
                        {line.rate !== undefined ? formatAmount(line.rate) : ''}
                      </TableCell>
                      <TableCell align="right">
                        {formatAmount(line.amount)}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteLine(line.id)}
                          aria-label="Delete line"
                        >
                          <DeleteIcon
                            fontSize="small"
                            sx={{ color: 'crimson' }}
                          />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ py: 2, textAlign: 'center' }}
            >
              Please fill above fields and click ADD. Quantity, days and rate are optional.
            </Typography>
          )}
          </Box>

          {invoiceDetails && totals && (
            <Box
              sx={{
                flexShrink: 0,
                borderTop: 1,
                borderColor: 'divider',
                pt: 2,
                pb: 0.5,
                mt: 'auto',
                bgcolor: 'background.paper',
              }}
            >
              <SummaryRow
                label="Taxable Amount"
                value={formatAmount(totals.taxableAmount)}
              />
              {invoiceDetails.igstRate > 0 ? (
                <SummaryRow
                  label={`IGST (${invoiceDetails.igstRate}%)`}
                  value={formatAmount(totals.igstValue)}
                />
              ) : (
                <>
                  <SummaryRow
                    label={`CGST (${invoiceDetails.cgstRate}%)`}
                    value={formatAmount(totals.cgstValue)}
                  />
                  <SummaryRow
                    label={`SGST (${invoiceDetails.sgstRate}%)`}
                    value={formatAmount(totals.sgstValue)}
                  />
                </>
              )}
              <SummaryRow
                label={noteValueLabel}
                value={formatAmount(totals.noteTotal)}
                emphasize
              />
              <Box sx={{ my: 1, borderTop: 1, borderColor: 'divider' }} />
              <SummaryRow
                label="Original Invoice Value"
                value={currencyFormatter.format(originalTotal)}
              />
              <SummaryRow
                label="Final Invoice Value"
                value={currencyFormatter.format(finalInvoiceValue)}
                emphasize
              />
            </Box>
          )}
        </Box>
      </Box>
    </AppDialog>
  )
}

export default DebitCreditNoteDialog
